import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Quote, CheckCircle2, Phone, Mail, Award, ShieldCheck } from "lucide-react";
import Layout from "@/components/site/Layout";
import PageHero from "@/components/site/PageHero";
import TrustBadges from "@/components/site/TrustBadges";
import Testimonials from "@/components/site/Testimonials";
import {
  TEAM,
  PLEDGE,
  MARY_OFFICE_URL,
  MARY_HEADSHOT_URL,
  HAPPY_CLIENT_URL,
  TEAM_GROUP_URL,
  TEAM_GATHERING_URL,
  ROG_WEST_LOGO_URL,
  CLIENT_EXPECTATIONS,
  WHY_CHOOSE,
} from "@/lib/assets";

const VALUES_DEPRECATED = [];

export default function AboutUs() {
  const founder = TEAM[0];
  const complianceTeam = TEAM.filter((m) => m.name === "Jannette Lomeli");
  const partnerTeam = TEAM.filter((m) => m.name === "Lorena Veintimilla" || m.name === "Michael Otto");
  const coordTeam = TEAM.filter((m) => m.name === "David Glanzrock" || m.name === "Kathey Rowlands");

  return (
    <Layout>
      <PageHero
        breadcrumb="Home / About Us"
        overline="Who We Are"
        title="A guide who understands"
        accentWord="both sides."
        description="We're a team of dedicated professionals who understand that selling a home is rarely just a transaction — especially when it follows the loss of a loved one. As Certified Probate Real Estate Specialists (CPRES), we built Probate Solution OC around a simple recognition: navigating legal responsibilities while grieving is hard, and families deserve a guide who understands both sides of that experience."
        image={MARY_OFFICE_URL}
      />

      {/* Mission block */}
      <section className="py-20 bg-white" data-testid="mission-block">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-navy-50/60 border border-navy-100 p-10 sm:p-14">
            <span className="section-overline">Our Mission</span>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl text-navy-900 leading-tight">
              Support, clarity, and resources — during <em className="italic text-gold-600">life's most difficult transitions</em>.
            </h2>
            <p className="mt-5 text-navy-700 leading-relaxed">
              Our mission is to give clients the support, clarity, and resources they need during one of life's most difficult transitions. That includes a comprehensive referral network — probate attorneys, accountants, landscapers, estate sale organizers, handymen, and home repair professionals — so you're never navigating this process alone. These services are always optional, but the network is there whenever you need it.
            </p>
          </div>
        </div>
      </section>

      {/* Founder feature — Mary */}
      <section className="py-20 bg-white" data-testid="founder-feature">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden">
              <img src={MARY_HEADSHOT_URL} alt="Mary Nguyen, Founder" className="absolute inset-0 h-full w-full object-cover object-center" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-900/45 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="glass rounded-2xl p-5">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-gold-700 font-semibold">Founder</div>
                  <div className="mt-1 font-heading text-2xl text-navy-900">{founder.name}</div>
                  <div className="text-xs text-navy-600 mt-0.5">{founder.role}</div>
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <span className="section-overline">Meet Mary</span>
            <h2 className="mt-3 font-heading text-4xl sm:text-5xl text-navy-900 leading-tight">
              A biologist, a data scientist, a business owner — <em className="italic text-gold-600">now your probate realtor</em>.
            </h2>
            <div className="relative mt-6 rounded-3xl bg-navy-50/50 border border-navy-100 p-7">
              <Quote size={32} className="absolute -top-4 left-6 text-gold-500" />
              <p className="text-navy-700 leading-relaxed">{founder.bio}</p>
              <p className="mt-4 text-navy-800 font-medium italic">
                If you're looking for a real estate partner who brings precision, compassion, and genuine expertise to every step of your journey, I'd be honored to guide you through it.
              </p>
            </div>
            <Link to="/contact" data-testid="founder-cta" className="mt-8 inline-flex btn-primary group">
              Book Time with Mary
              <ArrowRight size={16} className="ml-2 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why families trust us — with client couple photo */}
      <section className="py-24 bg-navy-50/40" data-testid="why-trust-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="relative aspect-[4/3] rounded-3xl overflow-hidden">
              <img src={HAPPY_CLIENT_URL} alt="Mary Nguyen with a happy Probate Solution OC client" className="absolute inset-0 h-full w-full object-cover object-top" />
              <div className="absolute top-5 left-5 glass rounded-full px-4 py-1.5 text-xs font-semibold text-navy-800 flex items-center gap-1.5">
                <Heart size={12} className="text-gold-600" />
                Real Clients · Real Trust
              </div>
            </div>
          </div>
          <div className="lg:col-span-6">
            <span className="section-overline">Why Families Trust Us</span>
            <h2 className="mt-3 font-heading text-4xl sm:text-5xl text-navy-900 leading-tight">
              Backed by a <em className="italic text-gold-600">trusted brokerage</em>.
            </h2>
            <p className="mt-4 text-navy-700 leading-relaxed">
              Integrity and reputation are the foundation of everything we do. We're proud to be affiliated with <span className="font-semibold text-navy-900">Realty ONE Group</span> — named to the Inc. 5000 list of fastest-growing companies for seven consecutive years. That reputation now extends across all 50 states and internationally.
            </p>
            <p className="mt-3 text-navy-700 leading-relaxed">
              To keep every transaction held to the highest standard, our office is backed by an on-site Compliance Officer and a Risk Management department available seven days a week.
            </p>
          </div>
        </div>

        {/* Client expectations checklist */}
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-16">
          <div className="text-center mb-10">
            <div className="text-xs uppercase tracking-[0.24em] text-gold-700 font-semibold">When you work with us, you can expect to:</div>
          </div>
          <div className="grid md:grid-cols-2 gap-3" data-testid="expectations-list">
            {CLIENT_EXPECTATIONS.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                data-testid={`expectation-${i}`}
                className="flex items-start gap-3 rounded-xl bg-white border border-navy-100 p-4 hover:border-gold-400 hover:shadow-sm transition-all"
              >
                <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-gold-600" />
                <span className={`text-sm text-navy-700 ${item.startsWith("Give love") ? "italic font-semibold text-gold-700" : ""}`}>{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why choose PSOC */}
      <section className="py-20 bg-white" data-testid="why-choose-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <span className="section-overline">Why Choose Probate Solution OC</span>
            <h2 className="mt-3 font-heading text-4xl sm:text-5xl text-navy-900 leading-tight">
              Six reasons families keep coming <em className="italic text-gold-600">back to us</em>.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHY_CHOOSE.map((r, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                data-testid={`why-choose-${i}`}
                className="group rounded-2xl border border-navy-100 bg-navy-50/40 p-6 hover:bg-navy-900 hover:border-navy-900 transition-all"
              >
                <div className="font-heading text-3xl text-gold-500 group-hover:text-gold-400 transition-colors">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <p className="mt-3 text-sm text-navy-700 group-hover:text-white leading-relaxed transition-colors">{r}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 8-point pledge */}
      <section className="py-20 bg-white" data-testid="pledge-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="section-overline">My Pledge to You</span>
              <h2 className="mt-3 font-heading text-4xl sm:text-5xl text-navy-900 leading-tight max-w-2xl">
                Eight promises that guide <em className="italic text-gold-600">every transaction</em>.
              </h2>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PLEDGE.map((p, i) => (
              <motion.div
                key={p.n}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: (i % 4) * 0.06 }}
                data-testid={`pledge-${i}`}
                className="group relative rounded-2xl border border-navy-100 bg-navy-50/40 p-6 hover:bg-navy-900 hover:border-navy-900 transition-all duration-300"
              >
                <div className="font-heading text-4xl text-gold-500 group-hover:text-gold-400 transition-colors">{p.n}</div>
                <h3 className="mt-2 font-heading text-lg text-navy-900 group-hover:text-white transition-colors">{p.t}</h3>
                <p className="mt-1.5 text-sm text-navy-600 leading-relaxed group-hover:text-white/70 transition-colors">{p.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team — grouped by role */}
      <section className="py-20 bg-navy-50/40">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="section-overline">Meet the Team</span>
              <h2 className="mt-3 font-heading text-4xl sm:text-5xl text-navy-900 leading-tight">
                Realtor, escrow, lending, title <em className="italic text-gold-600">and more</em>.
              </h2>
              <p className="mt-3 text-navy-600 max-w-xl">
                Each brings decades of experience to your family's transaction.
              </p>
            </div>
            <Award size={48} className="text-gold-500/40 hidden md:block" />
          </div>

          {/* Partner team */}
          <div className="text-xs uppercase tracking-[0.24em] text-navy-500 font-semibold mb-4">Escrow & Lending Partners</div>
          <div className="grid sm:grid-cols-2 gap-6 mb-14" data-testid="partner-team">
            {partnerTeam.map((m, i) => (
              <TeamCard key={m.name} m={m} idx={`partner-${i}`} />
            ))}
          </div>

          {/* Title & Coordination */}
          <div className="mt-8 flex items-center gap-3 mb-4">
            <Award size={16} className="text-gold-600" />
            <div className="text-xs uppercase tracking-[0.24em] text-navy-500 font-semibold">Title & Transaction Coordination</div>
          </div>
          <div className="grid sm:grid-cols-2 gap-6 mb-14" data-testid="coord-team">
            {coordTeam.map((m, i) => (
              <TeamCard key={m.name} m={m} idx={`coord-${i}`} />
            ))}
          </div>

          {/* Risk management & compliance */}
          <div className="mt-8 flex items-center gap-3 mb-4">
            <ShieldCheck size={16} className="text-gold-600" />
            <div className="text-xs uppercase tracking-[0.24em] text-navy-500 font-semibold">Risk Management & Compliance</div>
          </div>
          <div className="grid sm:grid-cols-2 gap-6" data-testid="compliance-team">
            {complianceTeam.map((m, i) => (
              <TeamCard key={m.name} m={m} idx={`compliance-${i}`} />
            ))}
          </div>
        </div>
      </section>

      <TrustBadges />
      <Testimonials />

      {/* Team gathering photo — real group shot */}
      <section className="py-20 bg-white" data-testid="team-gathering">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="section-overline">The Full Circle</span>
            <h2 className="mt-3 font-heading text-4xl sm:text-5xl text-navy-900 leading-tight">
              Realtor. Escrow. Lending. Title. <em className="italic text-gold-600">Under one roof</em>.
            </h2>
            <p className="mt-4 text-navy-600">
              A rare thing in real estate — the entire team behind your transaction, in the same room, on the same side.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <div className="relative rounded-3xl overflow-hidden aspect-square">
              <img src={TEAM_GROUP_URL} alt="Probate Solution OC team standing together" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-navy-900/60 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5">
                <div className="glass rounded-2xl px-4 py-2.5 inline-flex items-center gap-3">
                  <div className="h-2 w-2 rounded-full bg-gold-500 animate-pulse" />
                  <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-navy-800">The full team</span>
                </div>
              </div>
            </div>
            <div className="relative rounded-3xl overflow-hidden aspect-square">
              <img src={TEAM_GATHERING_URL} alt="Probate Solution OC team gathered around the conference table" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-navy-900/60 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5">
                <div className="glass rounded-2xl px-4 py-2.5 inline-flex items-center gap-3">
                  <div className="h-2 w-2 rounded-full bg-gold-500 animate-pulse" />
                  <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-navy-800">In the room, on your side</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why RealtyOneGroup band */}
      <section className="py-20 bg-navy-900 text-white text-center">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center justify-center bg-white rounded-full p-4 mb-6 shadow-2xl h-40 w-40">
            <img src={ROG_WEST_LOGO_URL} alt="Realty ONE Group West" className="h-full w-full object-contain" />
          </div>
          <span className="section-overline text-gold-400">Backed by Realty ONE Group WEST</span>
          <h2 className="mt-3 font-heading text-4xl sm:text-5xl leading-tight">
            Trusted nationwide. <em className="italic text-gold-400">Global by design.</em>
          </h2>
          <p className="mt-4 text-white/70 max-w-2xl mx-auto">
            Inc. 5000 fastest-growing companies for seven consecutive years. All 50 states and internationally.
          </p>
          <div className="mt-10 grid sm:grid-cols-3 gap-6" data-testid="rog-stats">
            {[
              { n: "Inc. 5000", l: "7 Consecutive Years" },
              { n: "50 States", l: "Plus International Reach" },
              { n: "22+ yrs", l: "Serving Orange County Families" },
            ].map((s) => (
              <div key={s.l} className="glass-dark rounded-2xl p-6">
                <div className="font-heading text-3xl text-gold-400">{s.n}</div>
                <div className="mt-2 text-xs uppercase tracking-[0.22em] text-white/70 font-semibold">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}

function TeamCard({ m, idx }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      data-testid={`team-${idx}`}
      className="group rounded-3xl overflow-hidden border border-navy-100 bg-white hover:shadow-xl transition-all"
    >
      <div className="grid grid-cols-3">
        <div className="col-span-1 relative aspect-square sm:aspect-auto">
          <img src={m.img} alt={m.name} className="absolute inset-0 h-full w-full object-cover" />
        </div>
        <div className="col-span-2 p-6">
          <div className="text-[10px] uppercase tracking-[0.24em] text-gold-700 font-semibold">{m.org}</div>
          <div className="mt-1 font-heading text-xl text-navy-900 leading-tight">{m.name}</div>
          <div className="text-xs text-navy-600 mt-0.5">{m.role}</div>
          <p className="mt-3 text-xs text-navy-600 leading-relaxed line-clamp-4">{m.bio}</p>
          <details className="mt-2 group/bio" data-testid={`team-${idx}-bio`}>
            <summary className="cursor-pointer list-none inline-flex items-center gap-1 text-[11px] font-semibold text-gold-700 hover:text-gold-600 transition-colors">
              <span className="group-open/bio:hidden">Read full biography</span>
              <span className="hidden group-open/bio:inline">Hide biography</span>
              <ArrowRight size={11} className="transition-transform group-open/bio:rotate-90" />
            </summary>
            <p className="mt-2 text-xs text-navy-600 leading-relaxed">{m.bio}</p>
          </details>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px] text-navy-700">
            {m.phone && (
              <a href={`tel:${m.phone.replace(/[^0-9]/g, "")}`} className="inline-flex items-center gap-1 hover:text-gold-600 transition-colors">
                <Phone size={11} className="text-gold-600" />
                {m.phone}
              </a>
            )}
            {m.email && (
              <a href={`mailto:${m.email}`} className="inline-flex items-center gap-1 hover:text-gold-600 transition-colors">
                <Mail size={11} className="text-gold-600" />
                {m.email}
              </a>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
